import React from "react";
import { useTranslation } from "react-i18next";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageHero from "@/components/site/PageHero";
import BottomCTA from "@/components/site/BottomCTA";
import useReveal from "@/hooks/useReveal";

export default function WhoWeAre() {
  useReveal();
  const { t } = useTranslation();
  const pillars = t("whoWeAre.pillars", { returnObjects: true });
  const paragraphs = t("whoWeAre.paragraphs", { returnObjects: true });
  const pillarImgs = [
    "/assets/1thymdqv_Than-Long.jpg",
    "/assets/ez9kvixa_chitta-center.jpg",
    "/assets/unsplash-1731458769726.jpg",
  ];

  return (
    <div className="min-h-screen bg-white text-unik-ink">
      <SiteNav />
      <PageHero
        eyebrow={t("whoWeAre.heroEyebrow")}
        title={t("whoWeAre.heroTitle")}
        accent={t("whoWeAre.heroAccent") ? <><br/>{t("whoWeAre.heroAccent")}</> : null}
        bgImage="/assets/9eitpmqk_hanoi-building.jpg"
      />

      {/* WHO WE ARE */}
      <section className="relative bg-white py-28 lg:py-40">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-20 items-stretch">
          <div className="lg:col-span-6" data-reveal>
            <div className="relative overflow-hidden h-full min-h-[420px]">
              <img
                src="/assets/5hxu7spx_image-aboutus.jpg"
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] hover:scale-105"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div data-reveal className="eyebrow text-[#14355a]">{t("whoWeAre.sectionEyebrow")}</div>
            <h2
              data-reveal
              style={{
                "--reveal-delay": "120ms",
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.0",
              }}
              className="mt-6 font-display text-[#14355a] tracking-tight"
            >
              {t("whoWeAre.sectionTitle")}<br/>{t("whoWeAre.sectionAccent")}{t("whoWeAre.sectionThird") && <><br/>{t("whoWeAre.sectionThird")}</>}
            </h2>
            <div
              data-reveal
              style={{ "--reveal-delay": "240ms" }}
              className="mt-8 space-y-6 max-w-xl text-[18px] lg:text-[19px] leading-[1.95] text-black"
            >
              {paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO — intro + 3 cards */}
      <section className="relative bg-[#f4f5f7] py-24 lg:py-32 overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div data-reveal className="max-w-3xl mb-16 lg:mb-20">
            <div className="eyebrow text-[#14355a]">{t("whoWeAre.whatWeDoEyebrow")}</div>
            <h2
              style={{
                fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
                fontWeight: 500,
                lineHeight: "1.0",
              }}
              className="mt-6 font-display text-[#14355a] tracking-tight"
            >
              {t("whoWeAre.whatWeDoTitle")}<br/>{t("whoWeAre.whatWeDoAccent")}
            </h2>
            <p className="mt-8 text-[18px] lg:text-[19px] leading-[1.95] text-black max-w-2xl">
              {t("whoWeAre.whatWeDoBody")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((w, i) => (
              <div
                key={w.n}
                data-reveal
                style={{ "--reveal-delay": `${i * 140}ms` }}
                className="group relative bg-white border border-[#14355a]/10 overflow-hidden transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(20,53,90,0.12)]"
                data-testid={`wwd-card-${w.n}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={pillarImgs[i]}
                    alt={w.t}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e2540] via-[#0e2540]/60 to-transparent" />
                  <div className="absolute top-7 left-7 flex items-center gap-3">
                    <span
                      className="font-display text-unik-gold tracking-tight"
                      style={{ fontSize: "clamp(2.5rem, 3.5vw, 3.5rem)", lineHeight: 1, fontWeight: 500 }}
                    >
                      {w.n}
                    </span>
                    <span className="block w-10 h-px bg-unik-gold/70" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-9 text-white">
                    <h3
                      style={{
                        fontSize: "clamp(1.375rem, 1.8vw, 1.75rem)",
                        fontWeight: 500,
                        lineHeight: 1.15,
                      }}
                      className="font-display tracking-tight"
                    >
                      {w.t}
                    </h3>
                    <p className="mt-5 text-[14.5px] leading-[1.75] text-white/85">
                      {w.b}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-display text-unik-gold opacity-90">
                      <span className="block w-6 h-px bg-unik-gold" />
                      {t("whoWeAre.pillarLabel")} {w.n}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCTA
        eyebrow={t("whoWeAre.bottomCta.eyebrow")}
        title={t("whoWeAre.bottomCta.title")}
        accent={t("whoWeAre.bottomCta.accent")}
        body={t("whoWeAre.bottomCta.body")}
        primary={{ label: t("whoWeAre.bottomCta.primary"), to: "/about/portfolio-companies" }}
        secondary={{ label: t("whoWeAre.bottomCta.secondary"), to: "/investments/approach" }}
      />
      <SiteFooter />
    </div>
  );
}
